import cn from 'classnames'
import { motion } from 'framer-motion'
import React, { useEffect, useMemo, useRef } from 'react'
import type { DragLayerMonitor } from 'react-dnd'
import { useDrag, useDragLayer } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'

import { KitSize } from '../KitTypes'
import * as s from './ArrayControl.module.scss'
import { getDragItemType, EMPTY_HOVER, NAVIGATION_GAP } from './constants'
import type {
  ArrayValue,
  FieldArrayDragItemType,
  IArrayChangeMeta,
  IRenderItemProps,
} from './types'

interface IFieldArrayDragItemProps<V extends ArrayValue> {
  name: string
  item: V
  hoverId: string | null
  index: number
  prevId: string | null
  setHoverId: (id: string | null) => void
  onDrop: (id: string) => void
  onChange?: (value: V[], meta?: IArrayChangeMeta<V>) => void
  RenderItem: React.FC<IRenderItemProps<V>>
  value: V[]
  max?: number
  min?: number
  size?: KitSize
  direction: 'vertical' | 'horizontal'
}

const FieldArrayDragItem = <V extends ArrayValue>({
  name,
  item,
  prevId,
  hoverId,
  index,
  setHoverId,
  onDrop,
  onChange,
  RenderItem,
  value,
  max,
  min,
  direction,
  ...rest
}: IFieldArrayDragItemProps<V>) => {
  const ref = useRef<HTMLDivElement>(null)
  const [, drag, preview] = useDrag({
    type: getDragItemType(name),
    item: () => ({ item, itemRef: ref.current, index }),
    end: (_data, monitor) => {
      if (!monitor.didDrop()) {
        onDrop(monitor.getItem().item.value)
      }

      setHoverId(EMPTY_HOVER)
    },
  })

  const { dragItem } = useDragLayer((monitor: DragLayerMonitor<FieldArrayDragItemType<V>>) => {
    const dragItem = monitor.getItem()?.item
    const isCurrentType = monitor.getItemType() === getDragItemType(name)
    if (!isCurrentType) {
      return { dragItem: null }
    }
    if (direction === 'vertical') {
      const { y } = monitor.getClientOffset() || { y: 0 }
      const { y: boxY, height: boxH } = ref.current?.getBoundingClientRect() || { y: 0, height: 0 }
      const showTopLine =
        (dragItem && y > boxY - NAVIGATION_GAP && y < boxY + boxH / 2) ||
        (dragItem && index === 0 && y < boxY + NAVIGATION_GAP)
      const showBottomLine = dragItem && y > boxY + boxH / 2 && y < boxY + boxH + NAVIGATION_GAP
      if (y && showTopLine) {
        setHoverId(prevId)
      } else if (showBottomLine && item) {
        setHoverId(item?.value)
      }
    }
    if (direction === 'horizontal') {
      const { x } = monitor.getClientOffset() || { x: 0 }
      const { x: boxX, width: boxW } = ref.current?.getBoundingClientRect() || { x: 0, width: 0 }
      const showLeftLine =
        (dragItem && x > boxX - NAVIGATION_GAP && x < boxX + boxW / 2) ||
        (dragItem && index === 0 && x < boxX + NAVIGATION_GAP)
      const showRightLine = dragItem && x > boxX + boxW / 2 && x < boxX + boxW + NAVIGATION_GAP
      if (x && showLeftLine) {
        setHoverId(prevId)
      } else if (showRightLine && item) {
        setHoverId(item?.value)
      }
    }

    return { dragItem }
  })

  const isDragging = useMemo(() => {
    if (!dragItem) {
      return false
    }

    return dragItem?.value === item.value
  }, [dragItem, item.value])

  const layoutDependency = useMemo(() => ({ dragItem, hoverId }), [hoverId, dragItem])

  useEffect(() => {
    preview(getEmptyImage())
  }, [preview])

  return (
    <motion.div
      className={cn(s.item, { [s.mask]: isDragging })}
      layout={isDragging ? false : 'position'}
      layoutDependency={layoutDependency}
      layoutId={isDragging ? undefined : item.value}
      ref={ref}
    >
      <RenderItem
        {...rest}
        drag={drag}
        index={index}
        isDragging={isDragging}
        item={item}
        max={max}
        min={min}
        name={name}
        onChange={onChange}
        value={value}
      />
    </motion.div>
  )
}

export default React.memo(FieldArrayDragItem) as typeof FieldArrayDragItem
